import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import PageHeading from '../../components/common/PageHeading';
import { getResetPath }      from '../../services/resetPath.service';
import { updateSectorItems } from '../../services/resetPath.service';
import api from '../../services/api';

// ─── Constants ─────────────────────────────────────────────────────────────────

const SECTORS = [
  { key: 'financial', label: 'Financial',  emoji: '💰' },
  { key: 'career',    label: 'Career',     emoji: '💼' },
  { key: 'emotional', label: 'Emotional',  emoji: '💛' },
  { key: 'mental',    label: 'Mental',     emoji: '🧠' },
  { key: 'physical',  label: 'Physical',   emoji: '💪' },
  { key: 'spiritual', label: 'Spiritual',  emoji: '🌿' },
];

// Step 1 sections — define what each zone looks like (all 4 zones, low to high)
const SECTIONS = [
  {
    key: 'rock_bottom',
    label: 'Rock Bottom looks like:',
    prompt: 'What does the lowest point look like for you in this area of life? This is your reference for "things are really bad."',
    placeholder: 'Add an item that describes rock bottom for you…',
  },
  {
    key: 'middle_ground',
    label: 'Middle Ground looks like:',
    prompt: 'What does it look like when you are working toward your goal — not quite there yet, but heading in the right direction?',
    placeholder: 'Add an item that describes your middle ground…',
  },
  {
    key: 'comfort_zone',
    label: 'My Goal — My Comfort Zone looks like:',
    prompt: null, // replaced by special note below
    placeholder: 'Add an item that describes your goal state…',
    isGoal: true,
  },
  {
    key: 'overabundance',
    label: 'Overabundance looks like:',
    prompt: 'What does it look like when you have too much — too much work, too much spending, too much stimulation?',
    placeholder: 'Add an item that describes overabundance for you…',
  },
];

// Step 2 only asks about middle ground and comfort zone — the zones where a member's
// current position matters most for determining if they're at goal or working toward it.
const ASSESSMENT_SECTIONS = ['middle_ground', 'comfort_zone'];

function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Continuums() {
  const [loading, setLoading]   = useState(true);
  const [openSectors, setOpenSectors] = useState({ financial: true });

  // items[sector][section] = [{id, text}, ...]
  const [items, setItems]       = useState({});
  // answers[sector] = {[itemId]: 'yes' | 'no'}
  const [answers, setAnswers]   = useState({});
  // newText[`${sector}_${section}`] = ''
  const [newText, setNewText]   = useState({});

  const [saving, setSaving]   = useState({});
  const [saved, setSaved]     = useState({});
  const [saveErr, setSaveErr] = useState({});
  // What they're actually doing, per sector, last 7 days (2026-09-29).
  const [doing, setDoing] = useState(null);
  useEffect(() => { api.get('/reset-path/doing').then(r => setDoing(r.data)).catch(() => {}); }, []);

  // ── Load data on mount ──────────────────────────────────────────────────────
  useEffect(() => {
    getResetPath()
      .then(data => {
        const p = data.path || {};
        const loadedItems = {};
        const loadedAnswers = {};

        SECTORS.forEach(({ key }) => {
          loadedItems[key] = {};
          SECTIONS.forEach(sec => {
            const dbKey = `${key}_${sec.key}_items`;
            const raw = p[dbKey];
            loadedItems[key][sec.key] = Array.isArray(raw) ? raw : [];
          });

          // Load current_position_items as answers map
          const posRaw = p[`${key}_current_position_items`];
          const posArr = Array.isArray(posRaw) ? posRaw : [];
          const ans = {};
          posArr.forEach(entry => {
            if (entry.item_id) ans[entry.item_id] = entry.answer;
          });
          loadedAnswers[key] = ans;
        });

        setItems(loadedItems);
        setAnswers(loadedAnswers);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // ── Item management ─────────────────────────────────────────────────────────
  const addItem = (sector, section) => {
    const k = `${sector}_${section}`;
    const text = (newText[k] || '').trim();
    if (!text) return;
    const item = { id: newId(), text };
    setItems(prev => ({
      ...prev,
      [sector]: {
        ...prev[sector],
        [section]: [...(prev[sector]?.[section] || []), item],
      },
    }));
    setNewText(prev => ({ ...prev, [k]: '' }));
  };

  const removeItem = (sector, section, id) => {
    setItems(prev => ({
      ...prev,
      [sector]: {
        ...prev[sector],
        [section]: (prev[sector]?.[section] || []).filter(i => i.id !== id),
      },
    }));
    // Also clear any answer for this item
    setAnswers(prev => {
      const sectorAns = { ...prev[sector] };
      delete sectorAns[id];
      return { ...prev, [sector]: sectorAns };
    });
  };

  const setAnswer = (sector, itemId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [sector]: { ...prev[sector], [itemId]: answer },
    }));
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async (sector) => {
    setSaving(p => ({ ...p, [sector]: true }));
    setSaveErr(p => ({ ...p, [sector]: '' }));
    setSaved(p => ({ ...p, [sector]: false }));

    const sectorItems = items[sector] || {};
    const sectorAnswers = answers[sector] || {};

    // Build current_position_items from Middle Ground and Comfort Zone answers only.
    // Rock bottom and overabundance items are reference points only, not position indicators.
    const assessmentSectionItems = ASSESSMENT_SECTIONS.flatMap(secKey => sectorItems[secKey] || []);
    const currentPositionItems = assessmentSectionItems
      .filter(item => sectorAnswers[item.id])
      .map(item => ({ item_id: item.id, answer: sectorAnswers[item.id] }));

    // Guard: if the member hasn't answered any items yet (either because they haven't
    // done the assessment or because state loaded before data arrived), send undefined
    // so the server's COALESCE keeps the existing position data intact.
    // Only send an empty array when there are assessment items to answer but none are
    // answered — meaning the member deliberately cleared everything.
    const positionItemsToSave = currentPositionItems.length > 0
      ? currentPositionItems
      : assessmentSectionItems.length > 0 ? [] : undefined;

    try {
      await updateSectorItems({
        sector,
        overabundance_items:      sectorItems.overabundance || [],
        middle_ground_items:      sectorItems.middle_ground || [],
        rock_bottom_items:        sectorItems.rock_bottom || [],
        comfort_zone_items:       sectorItems.comfort_zone || [],
        current_position_items:   positionItemsToSave,
      });
      setSaved(p => ({ ...p, [sector]: true }));
      setTimeout(() => setSaved(p => ({ ...p, [sector]: false })), 3000);
    } catch (err) {
      setSaveErr(p => ({
        ...p,
        [sector]: err?.response?.data?.error || 'Could not save. Please try again.',
      }));
    } finally {
      setSaving(p => ({ ...p, [sector]: false }));
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const allSectorItems = (sector) =>
    SECTIONS.flatMap(sec => (items[sector]?.[sec.key] || []).map(item => ({
      ...item,
      sectionKey: sec.key,
      sectionLabel: sec.label,
    })));

  // Step 2 only shows middle_ground and comfort_zone items
  const assessmentItems = (sector) =>
    SECTIONS
      .filter(sec => ASSESSMENT_SECTIONS.includes(sec.key))
      .flatMap(sec => (items[sector]?.[sec.key] || []).map(item => ({
        ...item,
        sectionKey: sec.key,
        sectionLabel: sec.label,
      })));

  const hasAnyItems = (sector) =>
    SECTIONS.some(sec => (items[sector]?.[sec.key] || []).length > 0);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-0">
        <div className="mb-6">
          <PageHeading title="My Continuums" />
          <p className="text-sm text-gray-500 mt-2">
            Define what each sector looks like in your own words. The system reflects your language back to you — never a score.
          </p>
        </div>

        {loading ? (
          <div className="space-y-4 animate-pulse">
            {[...Array(6)].map((_, i) => <div key={i} className="h-24 bg-gray-200 rounded-2xl" />)}
          </div>
        ) : (
          <div className="space-y-4">
            {SECTORS.map(({ key, label, emoji }) => (
              <SectorCard
                doing={doing?.[key]}
                key={key}
                sectorKey={key}
                label={label}
                emoji={emoji}
                open={!!openSectors[key]}
                onToggle={() => setOpenSectors(p => ({ ...p, [key]: !p[key] }))}
                sectorItems={items[key] || {}}
                answers={answers[key] || {}}
                newText={newText}
                hasAnyItems={hasAnyItems(key)}
                allItems={allSectorItems(key)}
                assessmentItems={assessmentItems(key)}
                saving={saving[key]}
                saved={saved[key]}
                saveErr={saveErr[key]}
                onAddItem={addItem}
                onRemoveItem={removeItem}
                onSetAnswer={setAnswer}
                onNewTextChange={(k, v) => setNewText(p => ({ ...p, [k]: v }))}
                onSave={() => handleSave(key)}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// ─── Sector Card ───────────────────────────────────────────────────────────────

function SectorCard({
  sectorKey, label, emoji, open, onToggle, doing,
  sectorItems, answers, newText, allItems, assessmentItems, hasAnyItems,
  saving, saved, saveErr,
  onAddItem, onRemoveItem, onSetAnswer, onNewTextChange, onSave,
}) {
  const comfortZoneItems = sectorItems.comfort_zone || [];
  const isSetUp = comfortZoneItems.length > 0;

  return (
    <div id={sectorKey} className="bg-white rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-2xl">{emoji}</span>
          <span className="font-bold text-reset-brown">{label}</span>
          {/* What you're doing beside where you say you are — Jenn, 2026-09-29. Plain counts, a gentle mirror. */}
          {doing && (
            <span className="text-xs text-reset-sage bg-reset-sage/10 rounded-full px-2.5 py-1">
              {doing.planned === 0
                ? 'No activities for this area yet — add one in My Plan'
                // Under half done: Jenn's wording (2026-09-29) — a nudge to plan
                // lighter, not a scolding. Otherwise the plain count.
                : doing.done * 2 < doing.planned
                  ? `Hey, you checked off ${doing.done} of the ${doing.planned} activities you scheduled. Let's try scheduling less.`
                  : `Last 7 days: you did ${doing.done} of the ${doing.planned} things you planned`}
            </span>
          )}
          {isSetUp && (
            <span className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-full px-2 py-0.5">
              Set up ✓
            </span>
          )}
        </div>
        <span className="text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-5 pb-6 space-y-8">

          {/* ── Step 1 ── */}
          <div className="pt-5">
            <h3 className="font-semibold text-reset-brown text-sm mb-1">Step 1 — Define your reference points</h3>
            <p className="text-xs text-gray-500 mb-5">
              Think of your {label.toLowerCase()} life as a see-saw. One end is overabundance, the other is rock bottom.
              Define each point in your own words by adding individual items. You can add as many or as few as you like.
            </p>

            <div className="space-y-6">
              {SECTIONS.map(sec => (
                <div key={sec.key}>
                  <label className="block text-xs font-semibold text-gray-700 mb-0.5">
                    {sec.label}
                  </label>

                  {sec.isGoal ? (
                    <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 mb-2 text-xs text-amber-800">
                      <strong>Your comfort zone is your goal.</strong> The system uses your words — not a number —
                      to understand where you are and support you toward this.
                      This is what you are actively working toward. Not perfect. Not thriving. Just where you feel balanced.
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mb-2">{sec.prompt}</p>
                  )}

                  {/* Existing items */}
                  <div className="space-y-1 mb-2">
                    {(sectorItems[sec.key] || []).map(item => (
                      <div key={item.id} className="flex items-center gap-2 py-1 group">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                        <span className="flex-1 text-sm text-gray-700">{item.text}</span>
                        <button
                          onClick={() => onRemoveItem(sectorKey, sec.key, item.id)}
                          className="text-gray-200 hover:text-red-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove"
                        >✕</button>
                      </div>
                    ))}
                  </div>

                  {/* Add input */}
                  <div className="flex gap-2">
                    <input
                      className="flex-1 text-sm border-b border-gray-200 bg-transparent focus:outline-none focus:border-amber-400 py-1 transition-colors"
                      placeholder={sec.placeholder}
                      value={newText[`${sectorKey}_${sec.key}`] || ''}
                      onChange={e => onNewTextChange(`${sectorKey}_${sec.key}`, e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), onAddItem(sectorKey, sec.key))}
                    />
                    <button
                      onClick={() => onAddItem(sectorKey, sec.key)}
                      className="text-amber-600 text-sm font-medium hover:text-amber-800 transition-colors px-1"
                    >
                      Add
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Step 2 ── */}
          <div>
            <h3 className="font-semibold text-reset-brown text-sm mb-1">Step 2 — Where are you right now?</h3>
            <p className="text-xs text-gray-500 mb-4">
              For each item below, answer honestly: is this currently true for you?
              We only ask about <strong>Middle Ground</strong> and <strong>My Goal</strong> —
              the two zones that matter most for understanding where you are on your continuum.
            </p>

            {assessmentItems.length === 0 ? (
              <p className="text-xs text-gray-400 italic">
                Add Middle Ground or My Goal items in Step 1 to answer them here.
              </p>
            ) : (
              <div className="space-y-4">
                {['middle_ground', 'comfort_zone'].map(sectionKey => {
                  const sectionItems = assessmentItems.filter(i => i.sectionKey === sectionKey);
                  if (sectionItems.length === 0) return null;
                  const sectionLabel = sectionKey === 'comfort_zone' ? 'My Goal — Comfort Zone' : 'Middle Ground';
                  return (
                    <div key={sectionKey}>
                      <p className="text-xs font-semibold text-gray-500 mb-1">{sectionLabel}</p>
                      <div className="divide-y divide-gray-50">
                        {sectionItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between py-2.5">
                            <span className="text-sm text-gray-700 flex-1 pr-4">{item.text}</span>
                            <div className="flex gap-2 flex-shrink-0">
                              <button
                                onClick={() => onSetAnswer(sectorKey, item.id, 'yes')}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                  answers[item.id] === 'yes'
                                    ? 'bg-green-100 text-green-700 border border-green-200'
                                    : 'text-gray-400 hover:text-green-600 border border-transparent hover:border-green-200'
                                }`}
                              >
                                Yes
                              </button>
                              <button
                                onClick={() => onSetAnswer(sectorKey, item.id, 'no')}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                  answers[item.id] === 'no'
                                    ? 'bg-red-50 text-red-500 border border-red-100'
                                    : 'text-gray-400 hover:text-red-400 border border-transparent hover:border-red-100'
                                }`}
                              >
                                No
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Step 3 ── */}
          <div>
            <h3 className="font-semibold text-reset-brown text-sm mb-1">Step 3 — Sector checklist items</h3>
            <p className="text-xs text-gray-500 mb-3">
              Add specific things that, when checked, tell you something about where you are on your continuum.
              These appear in your daily plan. Add them in{' '}
              <Link to="/dashboard/planning" className="text-amber-600 hover:underline">My Plan → Non-Negotiables</Link>.
            </p>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-xs text-gray-500">
              Your non-negotiables for {label} are managed in{' '}
              <Link to="/dashboard/planning" className="text-amber-600 hover:underline">My Plan</Link>.
            </div>
          </div>

          {/* ── Save ── */}
          {saveErr && (
            <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{saveErr}</p>
          )}
          {saved && (
            <p className="text-sm text-green-700 bg-green-50 rounded-xl px-4 py-2">✓ Saved</p>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="w-full bg-reset-brown text-white rounded-2xl py-3 font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {saving ? 'Saving…' : `Save my ${label} continuum`}
          </button>
        </div>
      )}
    </div>
  );
}
